import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { each } from 'lodash';
import { Button } from '@material-ui/core';
import useDebounce from '../../../hooks/useDebounce';
import TitleTypography from '../../MUI/TitleTypography';
import UserFormContent from '../../ContractSteps/UserFormContent';
import { clearEkengUser } from '../../../redux/modules/user';
import useUnRegisterValidationFields from '../../../hooks/useUnRegisterValidationFields';
import { findIsYerevan } from '../../../helpers/countryHelper';
import { YEREVAN_CITY_VILLAGE } from '../../../constants';

const MemberForm = ({
  contractType,
  member,
  memberBackendErrors,
  saveMember,
  formProps,
  cancelHandler,
  saveHandler,
  hasLanguageSensitiveNames,
  hasAddressInputsLanguageSensitiveNames,
  setIsFormOpen,
  handleFormSave,
  styles,
  handleResolveSpecificData,
  passportIdInfoComponent,
  removeMemberFromList,
}) => {
  const { t } = useTranslation();

  const { formContainer, commonStyles, classes, moduleStyles } = styles;
  const dispatch = useDispatch();
  const fields = [
    'firstName',
    'lastName',
    'firstNameEn',
    'lastNameEn',
    'birthday',
    'gender',
    'socCard',
    'passport',
    'passportFrom',
    'passportGiven',
    'passportValidUntil',
    'citizenship',
    'cellPhone',
    'phone',
    'country',
    'state',
    'cityVillage',
    'street',
    'streetEn',
  ];

  const [initialData, setInitialData] = useState(member);

  const {
    register,
    setValue,
    errors,
    triggerValidation,
    clearError,
    getValues,
    reset,
    unregister,
    watch,
    setError,
    handleSubmit,
  } = formProps;
  useUnRegisterValidationFields(fields, unregister);
  const currentFormData = useDebounce(watch());

  useEffect(
    () => () => {
      dispatch(clearEkengUser());
    },
    [dispatch],
  );

  useEffect(() => {
    setInitialData(member);
  }, [member]);

  useEffect(
    () => () => {
      reset();
    },
    [reset],
  );

  useEffect(() => {
    handleResolveSpecificData(setValue, member);
  }, [handleResolveSpecificData, member, setValue]);

  useEffect(() => {
    if (member.memberId.toString() === currentFormData.hiddenId && !member.isSaved) {
      dispatch(
        saveMember({
          ...member,
          ...currentFormData,
        }),
      );
    }
  }, [currentFormData, member, dispatch, saveMember]);

  useEffect(() => {
    if (memberBackendErrors && memberBackendErrors[member.memberId]) {
      const errorList = [];
      each(memberBackendErrors[member.memberId], (e, key) => {
        errorList.push({
          type: 'backend',
          name: key,
          message: `backendErrors.${e}`,
        });
      });
      setTimeout(() => setError(errorList));
    }
  }, [memberBackendErrors, member, setError]);

  return (
    <div className={formContainer} id={commonStyles.form}>
      <TitleTypography className={classes.sectionTitle}>
        {t(`${contractType}.newMember`)}
      </TitleTypography>
      <div>
        <form>
          <input name="hiddenId" ref={register} value={member.memberId} type="hidden" />
          <UserFormContent
            initialData={initialData}
            formProps={{
              register,
              getValues,
              setValue,
              errors,
              triggerValidation,
              clearError,
            }}
            hasLanguageSensitiveNames={hasLanguageSensitiveNames}
            hasAddressInputsLanguageSensitiveNames={hasAddressInputsLanguageSensitiveNames}
            passportIdInfoComponent={passportIdInfoComponent}
          />
          <div className={moduleStyles.fullWidthButtonWrapper}>
            <Button
              className={moduleStyles.button}
              variant="contained"
              color="primary"
              onClick={(e) => {
                const callback = () => {
                  saveHandler(initialData);
                  setInitialData({});
                  reset();
                  setIsFormOpen(false);

                  if (findIsYerevan(currentFormData.state)) {
                    currentFormData.cityVillage = YEREVAN_CITY_VILLAGE;
                  }

                  dispatch(saveMember(
                    { ...currentFormData, memberId: member.memberId, isSaved: true, resident: 1 },
                  ));
                };
                const data = fields.map((item) => ({
                  name: item,
                }));
                handleSubmit()();
                triggerValidation(data).then((response) => {
                  if (response) {
                    handleFormSave(e, callback);
                  }
                });
              }}
            >
              {t('save')}
            </Button>
          </div>
          <div className={moduleStyles.fullWidthButtonWrapper}>
            <Button
              className={moduleStyles.button}
              variant="outlined"
              color="primary"
              onClick={() => {
                if (member.isSaved) {
                  cancelHandler(currentFormData);
                } else {
                  removeMemberFromList(member);
                }
                setInitialData({});
                setIsFormOpen(false);
                reset();
              }}
            >
              {t('cancel')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

MemberForm.propTypes = {
  contractType: PropTypes.string.isRequired,
  member: PropTypes.shape({
    memberId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    isSaved: PropTypes.bool,
  }).isRequired,
  memberBackendErrors: PropTypes.arrayOf(PropTypes.shape({})),
  saveMember: PropTypes.func.isRequired,
  formProps: PropTypes.shape({
    register: PropTypes.func,
    setValue: PropTypes.func,
    getValues: PropTypes.func,
    triggerValidation: PropTypes.func,
    errors: PropTypes.shape({}),
    clearError: PropTypes.func,
    reset: PropTypes.func,
    unregister: PropTypes.func,
    watch: PropTypes.func,
    setError: PropTypes.func,
    handleSubmit: PropTypes.func,
  }).isRequired,
  cancelHandler: PropTypes.func,
  saveHandler: PropTypes.func,
  hasLanguageSensitiveNames: PropTypes.bool,
  hasAddressInputsLanguageSensitiveNames: PropTypes.bool,
  setIsFormOpen: PropTypes.func.isRequired,
  handleFormSave: PropTypes.func.isRequired,
  styles: PropTypes.shape({
    formContainer: PropTypes.string,
    commonStyles: PropTypes.shape({
      form: PropTypes.string,
    }),
    classes: PropTypes.shape({
      sectionTitle: PropTypes.string,
    }),
    moduleStyles: PropTypes.shape({
      fullWidthButtonWrapper: PropTypes.string,
      button: PropTypes.string,
    }),
  }).isRequired,
  handleResolveSpecificData: PropTypes.func,
  removeMemberFromList: PropTypes.func,
  passportIdInfoComponent: PropTypes.element,
};

MemberForm.defaultProps = {
  memberBackendErrors: null,
  cancelHandler: () => { },
  saveHandler: () => { },
  hasLanguageSensitiveNames: false,
  hasAddressInputsLanguageSensitiveNames: false,
  passportIdInfoComponent: undefined,
  handleResolveSpecificData: () => { },
  removeMemberFromList: () => { },
};

export default MemberForm;
